/* eslint-disable @nx/enforce-module-boundaries */
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { metaReducers, reducers } from './+state/reducers';
import { provideRouter, withRouterConfig } from '@angular/router';
import * as AuthStore from '@kanzi-apes/kanzi-authenticator';
import { KanziUtilsModule } from '@kanzi-apes/kanzi-utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  provideAngularQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { KanziWMSEnvironmentModel } from '@kanzi-apes/kanzi-models';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

/**
 * APP_INITIALIZER factory that preloads the saved language before the app renders.
 * Reads the language from localStorage, defaulting to Spanish ('es').
 * @param translate - The TranslateService used to load and apply the language
 * @returns A function returning a Promise that resolves once translations are loaded
 */
export function initializeTranslations(
  translate: TranslateService
): () => Promise<void> {
  return () => {
    const lang = localStorage.getItem('lang') || 'es';
    translate.setDefaultLang('es');
    return firstValueFrom(translate.use(lang)).then(() => undefined);
  };
}

const translateModule = TranslateModule.forRoot({
  loader: provideTranslateHttpLoader(),
  defaultLanguage: 'es',
});

export const appConfig: ApplicationConfig = {
  providers: [
    ConfirmationService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideAnimations(),
    provideHttpClient(withInterceptors([AuthStore.TokenWmsInterceptorFn])),
    importProvidersFrom(
      KanziUtilsModule.forRoot({ env: environment, wmsEnv: environment as unknown as KanziWMSEnvironmentModel })
    ),
    importProvidersFrom(
      StoreModule.forRoot(reducers, {
        metaReducers,
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      })
    ),
    importProvidersFrom(EffectsModule.forRoot([])),
    importProvidersFrom(
      StoreModule.forFeature(AuthStore.AUTH_FEATURE_KEY, AuthStore.authReducer)
    ),
    importProvidersFrom(EffectsModule.forFeature(AuthStore.AuthEffects)),

    importProvidersFrom(translateModule),
    provideAppInitializer(() => {
      const initializerFn = initializeTranslations(inject(TranslateService));
      return initializerFn();
    }),
    provideAngularQuery(new QueryClient()),
  ],
};
